import {ContactBase, ProjectCreate, ProjectPut, ProjectResponse, ProjectRoleID} from '@backend';
import {formatPhoneNumber, stripPhoneNumber} from '@validators/phone-number.validator';

/**
 * Form-ready model for a project.
 * Note: contacts are always stored in a ContactBase[] array; Backend may return ContactBase | ContactBase[] | null
 * Currently, only 0 (undefined) or 1 (ContactBase) is returned. This can support an array of contacts in the future.
 * Note: website is also stored in an array; this form model flattens it to a single value.
 */
export type ProjectCreateFormModel = ProjectCreate & {
  contacts?: ContactBase[];
  website?: string;
  principal_investigators?: string | string[];
};

// Generate a ProjectCreate given a ProjectResponse (ProjectCreate is the universal project form model)
export function projectResponseToProjectCreate(response: ProjectResponse): ProjectCreateFormModel {
  let contactsArray: ContactBase[];
  if (Array.isArray(response.contacts)) {
    contactsArray = response.contacts;
  } else if (response.contacts) {
    contactsArray = [response.contacts];
  } else {
    contactsArray = [{}];
  }
  const contacts = contactsArray.map(c => ({
    ...c,
    phone_number: c.phone_number ? formatPhoneNumber(c.phone_number) : c.phone_number,
  }));

  return {
    name: response.name,
    description: response.description,
    keywords: response.keywords ?? [],
    users: (response.project_users ?? []).map(u => ({
      email: u.user.email,
      role: u.project_role_id as ProjectRoleID,
      is_project_pi: u.is_project_pi,
    })),
    visible_to_all_users: response.visible_to_all_users ?? false,
    show_storage_organization_logo: response.show_storage_organization_logo ?? true,
    contacts: contacts,
    // optional fields
    partners: response.partners,
    funders: response.funders,
    associated_resource_ids: (response.associated_resource_ids ?? []).map(
      id => `${globalThis.origin}/resource-explorer/view/${id}`,
    ),

    // website handler: makes website field appear singular (not as an array)
    website: response.web_page_urls?.[0] ?? '',
    web_page_urls: response.web_page_urls ?? [],

    // Principal Investigators (completely virtual field)
    principal_investigators: response.project_users.filter(u => u.is_project_pi).map(u => u.user.email),
  };
}

// Generate ProjectCreate for project-add
export function defaultProjectCreate(): ProjectCreateFormModel {
  return {
    name: '',
    description: '',
    keywords: [],
    users: [],
    visible_to_all_users: false,
    show_storage_organization_logo: true,
    contacts: [{name: '', email: '', phone_number: ''}],
    partners: [],
    funders: [],
    web_page_urls: [],
    associated_resource_ids: [],
    principal_investigators: [],
  };
}

// Function that turns a ProjectFormModel into a ProjectPut (this prepares a project payload)
export function mapFormModelToProjectCreate(formModel: ProjectCreateFormModel): ProjectPut {
  const {website, web_page_urls, principal_investigators, associated_resource_ids, ...rest} = formModel;

  const pi_emails = Array.isArray(principal_investigators)
    ? principal_investigators
    : (principal_investigators ?? '').split(/[,; ]+\s*/).filter(Boolean);
  for (let pi of pi_emails) {
    if (!rest.users.some(u => u.email == pi))
      rest.users.push({email: pi, role: ProjectRoleID.OWNER, is_project_pi: true});
  }
  for (let u of rest.users) {
    u.is_project_pi = pi_emails.some(pi => pi == u.email);
  }

  if (rest.contacts) {
    rest.contacts = rest.contacts.map(c => ({
      ...c,
      phone_number: c.phone_number ? stripPhoneNumber(c.phone_number) : c.phone_number,
    }));
  }
  return {
    ...rest,
    // The last 36 characters in each URL should be the GUID identifying a resource.
    associated_resource_ids: (associated_resource_ids || []).map(url => url.slice(-36)),
    web_page_urls: typeof website === 'string' && website.trim().length > 0 ? [website.trim()] : [],
  };
}
